import { GROUND_BOUNCE } from '../lighting';
import {
  GRID_LINE_CORE,
  GRID_LINE_EDGE,
  MINOR_GRID_FADE_END,
  MINOR_GRID_FADE_START,
  MINOR_GRID_INK,
} from '../drapedGrid';
import { OUTLINE_CONTOUR_FULL, OUTLINE_CONTOUR_START } from '../outlines';

/**
 * WGSL for the surface pass.
 *
 * The struct here is the contract with `packSurfaceUniforms`, field for field and in order. Every
 * member is `vec4` or larger so that WGSL's alignment rules introduce no padding the packer would
 * have to know about - a `vec3` aligns to 16 bytes but sizes as 12, and the resulting offset drift
 * would not error, it would just read the wrong numbers.
 *
 * The lighting is a transcription of `getShadingTerms`, `applyShading` and `applyFog`, which are
 * tested on the CPU. Keeping the arithmetic identical is what makes the software renderer a usable
 * reference for this one: the two should agree pixel for pixel, so any difference is a bug rather
 * than a matter of taste.
 */
const toVec3 = (values: readonly number[]): string =>
  `vec3<f32>(${values.map(value => value.toFixed(6)).join(', ')})`;

export const SURFACE_SHADER = /* wgsl */ `
const MINIMUM_DIVISOR : f32 = 0.1;
const GROUND_BOUNCE : vec3<f32> = ${toVec3(GROUND_BOUNCE)};
const GRID_LINE_CORE : f32 = ${GRID_LINE_CORE.toFixed(6)};
const GRID_LINE_EDGE : f32 = ${GRID_LINE_EDGE.toFixed(6)};
const MINOR_GRID_INK : f32 = ${MINOR_GRID_INK.toFixed(6)};
const MINOR_GRID_FADE_START : f32 = ${MINOR_GRID_FADE_START.toFixed(6)};
const MINOR_GRID_FADE_END : f32 = ${MINOR_GRID_FADE_END.toFixed(6)};

struct Uniforms {
  viewProjection : mat4x4<f32>,
  basisRight     : vec4<f32>,
  basisUp        : vec4<f32>,
  basisForward   : vec4<f32>,
  // xyz world-space direction, w diffuse
  lightDirection : array<vec4<f32>, 2>,
  // rgb colour, w specular
  lightColor     : array<vec4<f32>, 2>,
  // rgb fog colour, w strength
  fog            : vec4<f32>,
  // ambient, shininess, shading, depthExtent
  params         : vec4<f32>,
  // zMin, zSpan, heightScale, cel steps (0 is smooth shading)
  field          : vec4<f32>,
  // draped grid along x: first, spacing, count, minor divisions
  gridX          : vec4<f32>,
  // draped grid along y: first, spacing, count, minor divisions
  gridY          : vec4<f32>,
  // rgb ink, w opacity - 0 draws no grid
  gridInk        : vec4<f32>,
  // x 1 to color from the paint texture rather than the ramp; yzw unused
  paint          : vec4<f32>,
};

@group(0) @binding(0) var<uniform> uniforms : Uniforms;
@group(0) @binding(1) var rampTexture : texture_2d<f32>;
@group(0) @binding(2) var rampSampler : sampler;
@group(0) @binding(3) var paintTexture : texture_2d<f32>;
@group(0) @binding(4) var paintSampler : sampler;

struct VertexOut {
  @builtin(position) clipPosition : vec4<f32>,
  @location(0) normal : vec3<f32>,
  @location(1) value  : f32,
  @location(2) depth  : f32,
  @location(3) plot   : vec2<f32>,
};

@vertex
fn vertexMain(
  @location(0) position : vec3<f32>,
  @location(1) normal   : vec3<f32>,
  @location(2) value    : f32,
) -> VertexOut {
  var out : VertexOut;

  // Height exaggeration lives in the matrix, so positions arrive with z in [0, 1] and the vertex
  // buffer survives a change to the relief dial. The normals do not: they are computed against a
  // scaled copy on the CPU, because a steeper surface genuinely has different normals.
  let clip = uniforms.viewProjection * vec4<f32>(position, 1.0);

  // Depth along the view axis, which the matrix does not carry - its third row is zero, because the
  // software path computes depth separately rather than through clip space.
  let scaled = vec3<f32>(position.xy, position.z * uniforms.field.z);
  let depth = dot(uniforms.basisForward.xyz, scaled);
  let extent = uniforms.params.w;

  // Nearer the eye means larger depth, and the pipeline compares depth with 'less', so nearness has
  // to be inverted into clip z. Multiplied by w because clip z is divided by w alongside x and y.
  let normalisedDepth = clamp((extent - depth) / (2.0 * extent), 0.0, 1.0);

  // Floored to match both software paths, which divide by max(MINIMUM_DIVISOR, w): without it a
  // vertex past perspective * depth > 1 divides by a negative w here and inverts through the
  // origin, while the software renderer clamps the foreshortening and keeps drawing. Only w is
  // clamped - scaling xy alongside it would cancel the clamp, since the hardware divides by w.
  let clipW = max(MINIMUM_DIVISOR, clip.w);

  out.clipPosition = vec4<f32>(clip.xy, normalisedDepth * clipW, clipW);
  out.normal = normal;
  out.value = value;
  out.depth = depth;
  out.plot = position.xy;
  return out;
}

/** Mirrors \`toCelLambert\`: a diffuse term rounded to the nearest of \`steps\` bands. */
fn toCelLambert(lambert : f32, steps : f32) -> f32 {
  if (steps <= 0.0) {
    return lambert;
  }
  return round(lambert * steps) / steps;
}

/** Mirrors \`toGridLineCoverage\`: the nearest line of a family, measured in pixels. */
fn toGridLineCoverage(coordinate : f32, axis : vec4<f32>, footprint : f32) -> f32 {
  let spacing = axis.y;
  if (spacing <= 0.0 || footprint <= 0.0) {
    return 0.0;
  }
  let nearest = clamp(round((coordinate - axis.x) / spacing), 0.0, axis.z);
  let distance = abs(coordinate - (axis.x + nearest * spacing)) / footprint;
  return 1.0 - smoothstep(GRID_LINE_CORE, GRID_LINE_EDGE, distance);
}

/** Mirrors \`toDrapedGridCoverage\`: tick lines, and minor lines faded where they crowd. */
fn toDrapedGridCoverage(coordinate : f32, axis : vec4<f32>, footprint : f32) -> f32 {
  let major = toGridLineCoverage(coordinate, axis, footprint);
  if (axis.w <= 1.0 || footprint <= 0.0) {
    return major;
  }
  let minorSpacing = axis.y / axis.w;
  let crowding = smoothstep(MINOR_GRID_FADE_START, MINOR_GRID_FADE_END, minorSpacing / footprint);
  let minor = toGridLineCoverage(
    coordinate,
    vec4<f32>(axis.x, minorSpacing, axis.z * axis.w, 1.0),
    footprint
  );
  return max(major, minor * MINOR_GRID_INK * crowding);
}

@fragment
fn fragmentMain(
  in : VertexOut,
  @builtin(front_facing) isFront : bool,
) -> @location(0) vec4<f32> {
  // Renormalised per fragment, which is what makes this Phong rather than Gouraud: interpolating
  // unit vectors does not yield a unit vector, and skipping this shows as a highlight that dims
  // toward the middle of every triangle.
  let normal = normalize(in.normal) * select(-1.0, 1.0, isFront);
  let forward = uniforms.basisForward.xyz;

  let ambient = uniforms.params.x;
  let shininess = uniforms.params.y;
  let shadingAmount = uniforms.params.z;

  let bounce = mix(GROUND_BOUNCE, vec3<f32>(1.0), (normal.z + 1.0) / 2.0);
  var intensity = select(vec3<f32>(ambient) * bounce, vec3<f32>(ambient), isFront);
  var highlight = vec3<f32>(0.0, 0.0, 0.0);

  for (var index = 0u; index < 2u; index = index + 1u) {
    let direction = uniforms.lightDirection[index].xyz;
    let diffuse = uniforms.lightDirection[index].w;
    let color = uniforms.lightColor[index].rgb;
    let specular = uniforms.lightColor[index].w;

    let lambert = toCelLambert(max(0.0, dot(normal, direction)), uniforms.field.w);
    // Blinn's halfway vector: one normalise instead of a reflection, indistinguishable here.
    let halfway = normalize(direction + forward);
    let specularTerm = specular * pow(max(0.0, dot(normal, halfway)), shininess);

    intensity = intensity + diffuse * lambert * color;
    highlight = highlight + specularTerm * color;
  }

  // The shading dial mixes the terms rather than the resulting colours, so 0 is exactly the flat
  // ramp colour rather than approximately it.
  intensity = vec3<f32>(1.0) + (intensity - vec3<f32>(1.0)) * shadingAmount;
  highlight = highlight * shadingAmount;

  // The ramp is a baked texture rather than arithmetic, so the shader samples exactly the bytes the
  // software renderer computes - including the linear-light interpolation and any band quantisation.
  let normalisedValue = clamp(
    (in.value - uniforms.field.x) / uniforms.field.y,
    0.0,
    1.0
  );
  let rampColor = textureSample(
    rampTexture,
    rampSampler,
    vec2<f32>(normalisedValue, 0.5)
  ).rgb;
  // Mirrors \`samplePaint\`: row 0 of the paint is cube y = -1. Sampled either way and selected
  // after, since a texture sample has to sit in uniform control flow.
  let paintColor = textureSample(
    paintTexture,
    paintSampler,
    (in.plot + vec2<f32>(1.0)) * 0.5
  ).rgb;
  let baseColor = select(rampColor, paintColor, uniforms.paint.x > 0.5);

  var lit = clamp(baseColor * intensity + highlight, vec3<f32>(0.0), vec3<f32>(1.0));

  // Inked onto the surface before the fog, so the grid recedes with what it is drawn on. The
  // footprints are taken here in uniform control flow, where derivatives are defined.
  let footprint = fwidth(in.plot);
  let gridAmount = max(
    toDrapedGridCoverage(in.plot.x, uniforms.gridX, footprint.x),
    toDrapedGridCoverage(in.plot.y, uniforms.gridY, footprint.y)
  ) * uniforms.gridInk.w;
  lit = mix(lit, uniforms.gridInk.rgb, gridAmount);

  // Aerial perspective, applied last: atmosphere sits between the eye and the surface rather than
  // being a property of it.
  let extent = uniforms.params.w;
  let nearness = clamp((in.depth + extent) / (2.0 * extent), 0.0, 1.0);
  let fogAmount = uniforms.fog.w * (1.0 - nearness);
  lit = mix(lit, uniforms.fog.rgb, fogAmount);

  return vec4<f32>(lit, 1.0);
}
`;

/**
 * WGSL for the outline pass: a transcription of `toOutlineMask`, reading the surface pass's depth.
 *
 * The surface pass writes clip z as `(extent - depth) / (2 * extent)`, an affine function of the
 * view-axis depth the software path keeps, so its second difference is the software one scaled by
 * `-1 / 2`: nearer is smaller, and one clip unit is two extents. A pixel the surface never reached
 * keeps the clear value of 1.
 */
export const OUTLINE_SHADER = /* wgsl */ `
const CONTOUR_START : f32 = ${OUTLINE_CONTOUR_START.toFixed(6)};
const CONTOUR_FULL : f32 = ${OUTLINE_CONTOUR_FULL.toFixed(6)};

@group(0) @binding(0) var surfaceDepth : texture_depth_2d;
// rgb ink, w opacity
@group(0) @binding(1) var<uniform> ink : vec4<f32>;

@vertex
fn outlineVertex(@builtin(vertex_index) vertexIndex : u32) -> @builtin(position) vec4<f32> {
  var corners = array<vec2<f32>, 3>(
    vec2<f32>(-1.0, -1.0),
    vec2<f32>(3.0, -1.0),
    vec2<f32>(-1.0, 3.0)
  );
  return vec4<f32>(corners[vertexIndex], 0.0, 1.0);
}

fn isEmpty(depth : f32) -> bool {
  return depth >= 1.0;
}

@fragment
fn outlineFragment(@builtin(position) position : vec4<f32>) -> @location(0) vec4<f32> {
  let size = vec2<i32>(textureDimensions(surfaceDepth));
  let pixel = vec2<i32>(position.xy);
  let center = textureLoad(surfaceDepth, pixel, 0);
  if (isEmpty(center)) {
    discard;
  }

  let hasLeft = pixel.x > 0;
  let hasRight = pixel.x < size.x - 1;
  let hasUp = pixel.y > 0;
  let hasDown = pixel.y < size.y - 1;
  let left = select(center, textureLoad(surfaceDepth, pixel - vec2<i32>(1, 0), 0), hasLeft);
  let right = select(center, textureLoad(surfaceDepth, pixel + vec2<i32>(1, 0), 0), hasRight);
  let up = select(center, textureLoad(surfaceDepth, pixel - vec2<i32>(0, 1), 0), hasUp);
  let down = select(center, textureLoad(surfaceDepth, pixel + vec2<i32>(0, 1), 0), hasDown);

  var mask = 0.0;
  if (isEmpty(left) || isEmpty(right) || isEmpty(up) || isEmpty(down)) {
    mask = 1.0;
  } else {
    let across = select(0.0, 2.0 * center - left - right, hasLeft && hasRight);
    let along = select(0.0, 2.0 * center - up - down, hasUp && hasDown);
    mask = smoothstep(CONTOUR_START, CONTOUR_FULL, -2.0 * (across + along));
  }

  // Premultiplied, to blend over the surface with \`one, one-minus-src-alpha\`.
  let amount = mask * ink.w;
  return vec4<f32>(ink.rgb * amount, amount);
}
`;
